import { useEffect, useMemo, useState } from 'react';

import { Empty } from './ui';
import { api } from '../lib/api';

/**
 * The OpenAPI document view: one operation in full, or the whole spec.
 *
 * Rendered from the stored document rather than a Swagger UI dependency. Three
 * reasons, in order of how much they would have cost:
 *
 *   1. There is no "Try it out" button, and there must not be. These are live
 *      client APIs — FrontDeskIQ answering real calls for ABC Corporation — and
 *      a request-firing control sitting in an internal ops tool is a loaded gun.
 *      Every off-the-shelf viewer ships one and it has to be switched off; here
 *      it was never built.
 *   2. No new npm dependency. Adding one can reshuffle the hoisted tree and
 *      break runtime module resolution, which this workspace has done before and
 *      which a clean build does not catch.
 *   3. It uses the app's own tokens, so it reads as part of PlatformIQ and works
 *      in both themes without importing a stylesheet that fights them.
 *
 * The document is served from storage, not fetched live. That is deliberate: the
 * container may be stopped or the host unreachable, and reading an API is most
 * useful exactly then. When it was captured is shown rather than implied.
 */

type Node = Record<string, unknown>;

const asObj = (v: unknown): Node | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Node) : null;
const asStr = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/**
 * Follows a JSON Pointer inside this document.
 *
 * Internal refs only. An external `$ref` would be another HTTP request, from the
 * browser, to a host that is probably on a private address — so it is reported
 * as unresolved rather than attempted and silently failing.
 */
function pointer(doc: Node, ref: string): unknown {
  if (!ref.startsWith('#/')) return undefined;
  return ref
    .slice(2)
    .split('/')
    .reduce<unknown>((cur, raw) => {
      // ~1 and ~0 are the JSON Pointer escapes for / and ~, and they appear for
      // real here: a schema keyed on a path like "/chat/{id}" is not unusual.
      const part = decodeURIComponent(raw).replace(/~1/g, '/').replace(/~0/g, '~');
      const o = asObj(cur);
      return o ? o[part] : undefined;
    }, doc as unknown);
}

/**
 * Resolves a `$ref` chain to the schema it names.
 *
 * `seen` is per-chain and not shared, so a document that legitimately references
 * the same schema twice still resolves both. It exists for self-referential
 * schemas — a tree node whose children are tree nodes — which are common and
 * would otherwise spin forever.
 */
function resolve(doc: Node, node: unknown, seen: Set<string> = new Set()): Node | null {
  let cur = asObj(node);
  while (cur) {
    const ref = asStr(cur.$ref);
    if (!ref) return cur;
    if (seen.has(ref)) return { ...cur, 'x-piq-circular': ref };
    seen.add(ref);
    const next = asObj(pointer(doc, ref));
    if (!next) return { 'x-piq-unresolved': ref };
    // A sibling `description` alongside a $ref is meaningful in OpenAPI 3.1 and
    // is lost if the target simply replaces the node, so it is carried over.
    cur = cur.description ? { ...next, description: cur.description } : next;
  }
  return null;
}

/** The name a schema goes by, for a type label: `#/components/schemas/Foo` → Foo. */
function refName(node: Node | null): string | null {
  const ref = node ? asStr(node.$ref) : null;
  return ref ? (ref.split('/').pop() ?? null) : null;
}

/**
 * A one-line type for a schema: `string`, `Foo[]`, `string | null`, `integer`.
 *
 * Unions are rendered rather than collapsed to "object". `anyOf: [X, null]` is
 * how both FastAPI and OpenAPI 3.1 spell "optional X", and showing that as
 * "object" would hide the actual type of most optional fields in this estate.
 */
function typeLabel(doc: Node, raw: unknown, depth = 0): string {
  const direct = asObj(raw);
  const named = refName(direct);
  if (named) return named;

  const node = resolve(doc, raw);
  if (!node) return 'any';
  if (node['x-piq-unresolved']) return String(node['x-piq-unresolved']).split('/').pop() ?? 'unknown';

  for (const key of ['anyOf', 'oneOf', 'allOf'] as const) {
    const branches = asArr(node[key]);
    if (branches.length > 0 && depth < 4) {
      const parts = branches.map((b) => typeLabel(doc, b, depth + 1));
      // null is a branch, not a type worth listing twice.
      const unique = [...new Set(parts)];
      return key === 'allOf' ? unique.join(' & ') : unique.join(' | ');
    }
  }

  const enumValues = asArr(node.enum);
  if (enumValues.length > 0 && enumValues.length <= 6) {
    return enumValues.map((v) => JSON.stringify(v)).join(' | ');
  }

  const t = asStr(node.type) ?? (Array.isArray(node.type) ? asArr(node.type).join(' | ') : null);
  if (t === 'array') {
    const items = node.items;
    return depth < 4 ? `${typeLabel(doc, items, depth + 1)}[]` : 'array';
  }
  if (t) return asStr(node.format) ? `${t} (${asStr(node.format)})` : t;
  if (node.properties) return 'object';
  return 'any';
}

/**
 * A schema's fields, expandable.
 *
 * Collapsed past the first level by default and hard-stopped at depth 6. A
 * fully expanded FastAPI schema is thousands of rows — the component schemas
 * reference each other — and a wall of them answers no question.
 */
function SchemaFields({ doc, raw, depth = 0 }: { doc: Node; raw: unknown; depth?: number }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const node = resolve(doc, raw);
  if (!node) return <p className="muted small">No schema.</p>;

  if (node['x-piq-circular']) {
    return <p className="muted small">↻ references {String(node['x-piq-circular']).split('/').pop()}</p>;
  }
  if (node['x-piq-unresolved']) {
    return (
      <p className="muted small">
        Unresolved reference <code>{String(node['x-piq-unresolved'])}</code> — the document does not
        define it.
      </p>
    );
  }

  // allOf is how OpenAPI spells inheritance. Merging the branches' properties is
  // what the reader means by "the fields of this thing"; showing three separate
  // tables would make them reassemble it mentally.
  const merged: Node = { ...node };
  const allOf = asArr(node.allOf);
  if (allOf.length > 0) {
    const props: Node = {};
    const required: unknown[] = [];
    for (const branch of allOf) {
      const b = resolve(doc, branch);
      if (!b) continue;
      Object.assign(props, asObj(b.properties) ?? {});
      required.push(...asArr(b.required));
    }
    Object.assign(props, asObj(node.properties) ?? {});
    merged.properties = props;
    merged.required = [...required, ...asArr(node.required)];
  }

  // An array at the top level: describe the element, since that is the shape the
  // caller actually has to build.
  if (asStr(merged.type) === 'array' && !merged.properties) {
    return (
      <>
        <p className="muted small">An array of {typeLabel(doc, merged.items)}:</p>
        <SchemaFields doc={doc} raw={merged.items} depth={depth + 1} />
      </>
    );
  }

  const properties = asObj(merged.properties);
  if (!properties || Object.keys(properties).length === 0) {
    const label = typeLabel(doc, merged);
    return <p className="muted small">{label === 'any' ? 'No declared shape.' : <code>{label}</code>}</p>;
  }

  const required = new Set(asArr(merged.required).filter((r): r is string => typeof r === 'string'));

  return (
    <table className="data-table schema-table">
      <thead>
        <tr>
          <th scope="col">Field</th>
          <th scope="col">Type</th>
          <th scope="col">Description</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(properties).map(([name, value]) => {
          const child = resolve(doc, value);
          const isRequired = required.has(name);
          // Only offer expansion where there is something underneath. An
          // affordance that opens an empty panel teaches people to stop clicking.
          const childProps = child ? asObj(child.properties) : null;
          const childItems = child && asStr(child.type) === 'array' ? resolve(doc, child.items) : null;
          const nestable =
            depth < 6 &&
            ((childProps && Object.keys(childProps).length > 0) ||
              (childItems && Object.keys(asObj(childItems.properties) ?? {}).length > 0));
          const isOpen = open.has(name);

          return (
            <tr key={name}>
              <td>
                {nestable ? (
                  <button
                    type="button"
                    className="linklike mono"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpen((s) => {
                        const next = new Set(s);
                        if (next.has(name)) next.delete(name);
                        else next.add(name);
                        return next;
                      })
                    }
                  >
                    {isOpen ? '▾' : '▸'} {name}
                  </button>
                ) : (
                  <code>{name}</code>
                )}
                {isRequired ? (
                  <span className="tag tag-req" title="Required">required</span>
                ) : (
                  <span className="muted small"> optional</span>
                )}
              </td>
              <td className="mono small">{typeLabel(doc, value)}</td>
              <td className="small">
                {asStr(child?.description) ?? <span className="muted">—</span>}
                {child && child.default !== undefined && (
                  <div className="muted small">default: <code>{JSON.stringify(child.default)}</code></div>
                )}
                {isOpen && (
                  <div className="schema-nested">
                    <SchemaFields
                      doc={doc}
                      raw={asStr(child?.type) === 'array' ? child?.items : value}
                      depth={depth + 1}
                    />
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** One operation, rendered from the document: parameters, body, responses. */
function OperationDetail({ doc, method, path }: { doc: Node; method: string; path: string }) {
  const paths = asObj(doc.paths);
  const item = asObj(paths?.[path]);
  const op = asObj(item?.[method.toLowerCase()]);

  if (!op) {
    return (
      <Empty>
        <code>
          {method} {path}
        </code>{' '}
        is not in this document. The stored spec is from a different version of
        this deployment than the operation row.
      </Empty>
    );
  }

  // Path-level parameters apply to every method on that path, so an operation
  // that declares none can still take them. Reading only the operation's own
  // list is the usual way to lose path parameters entirely.
  const params = [...asArr(item?.parameters), ...asArr(op.parameters)]
    .map((p) => resolve(doc, p))
    .filter((p): p is Node => p !== null);

  const body = asObj(resolve(doc, op.requestBody));
  const bodyContent = asObj(body?.content);
  const bodyType = bodyContent ? Object.keys(bodyContent)[0] : null;
  const bodySchema = bodyType ? asObj(bodyContent?.[bodyType])?.schema : null;

  const responses = asObj(op.responses);

  return (
    <div className="api-op">
      <div className="api-op-head">
        <span className={`method method-${method.toLowerCase()}`}>{method}</span>
        <code className="api-op-path">{path}</code>
      </div>

      {asStr(op.summary) && <p className="api-op-summary">{asStr(op.summary)}</p>}
      {asStr(op.description) && <p className="muted small">{asStr(op.description)}</p>}
      {op.deprecated === true && <p className="notice">This operation is marked deprecated in the spec.</p>}

      <h3>Parameters</h3>
      {params.length === 0 ? (
        <p className="muted small">None.</p>
      ) : (
        <table className="data-table schema-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">In</th>
              <th scope="col">Type</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {params.map((p) => (
              <tr key={`${asStr(p.in)}:${asStr(p.name)}`}>
                <td>
                  <code>{asStr(p.name)}</code>
                  {p.required === true ? (
                    <span className="tag tag-req">required</span>
                  ) : (
                    <span className="muted small"> optional</span>
                  )}
                </td>
                <td className="small">{asStr(p.in)}</td>
                <td className="mono small">{typeLabel(doc, p.schema ?? p)}</td>
                <td className="small">{asStr(p.description) ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Request body</h3>
      {!bodySchema ? (
        <p className="muted small">None.</p>
      ) : (
        <>
          <p className="muted small">
            <code>{bodyType}</code>
            {body?.required === true && ' · required'}
            {refName(asObj(bodySchema)) && <> · schema <code>{refName(asObj(bodySchema))}</code></>}
          </p>
          <SchemaFields doc={doc} raw={bodySchema} />
        </>
      )}

      <h3>Responses</h3>
      {!responses || Object.keys(responses).length === 0 ? (
        <p className="muted small">Not documented.</p>
      ) : (
        Object.entries(responses).map(([code, raw]) => {
          const r = asObj(resolve(doc, raw));
          const content = asObj(r?.content);
          const ct = content ? Object.keys(content)[0] : null;
          const schema = ct ? asObj(content?.[ct])?.schema : null;
          return (
            <div key={code} className="api-response">
              <div className="api-response-head">
                <span className={`tag ${code.startsWith('2') ? 'tag-ok' : 'tag-warn'}`}>{code}</span>
                <span className="small">{asStr(r?.description) ?? ''}</span>
                {ct && <span className="muted small mono">{ct}</span>}
              </div>
              {schema ? <SchemaFields doc={doc} raw={schema} depth={1} /> : null}
            </div>
          );
        })
      )}
    </div>
  );
}

interface SpecDoc {
  sourceUrl: string;
  title: string | null;
  apiVersion: string | null;
  specFormat: string | null;
  body: string;
  byteSize: number;
  operationCount: number;
  fetchedAt: string;
}

interface SpecResponse {
  id: string;
  name: string;
  version: string | null;
  product: { key: string; name: string };
  client: { code: string } | null;
  server: { name: string };
  apiSpecs: SpecDoc[];
}

/**
 * Loads and shows one deployment's stored spec, focused on one operation.
 *
 * `focus` is the operation the reader clicked. Without it the whole document is
 * listed, which is the "show me this API" case rather than "show me this
 * endpoint".
 */
export function ApiSpecView({
  deploymentId,
  focus,
}: {
  deploymentId: string;
  focus?: { method: string; path: string };
}) {
  const [data, setData] = useState<SpecResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [which, setWhich] = useState(0);
  const [raw, setRaw] = useState(false);
  const [selected, setSelected] = useState<{ method: string; path: string } | null>(focus ?? null);

  useEffect(() => {
    setData(null);
    setError(null);
    api<SpecResponse>(`/deployments/${encodeURIComponent(deploymentId)}/spec`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the spec'));
  }, [deploymentId]);

  useEffect(() => setSelected(focus ?? null), [focus?.method, focus?.path]);

  const spec = data?.apiSpecs[which] ?? null;

  // Parsed once per document, not per render: these run to a few hundred KB and
  // JSON.parse of that on every keystroke in the filter box is felt.
  const doc = useMemo<Node | null>(() => {
    if (!spec) return null;
    try {
      return asObj(JSON.parse(spec.body));
    } catch {
      return null;
    }
  }, [spec?.body]);

  if (error) return <p className="form-error">{error}</p>;
  if (!data) return <p className="muted small">Loading spec…</p>;
  if (data.apiSpecs.length === 0) {
    return (
      <Empty>
        No spec stored for this deployment yet. PlatformIQ captures each container's OpenAPI
        document during API discovery — run it from the Products page, or this service does not
        serve one.
      </Empty>
    );
  }
  // `spec` is narrowed alongside `doc` because the two are derived together:
  // there is no document without a spec row to read it from.
  if (!spec || !doc) {
    return <p className="form-error">The stored document is not valid JSON and cannot be rendered.</p>;
  }

  const operations: Array<{ method: string; path: string; summary: string | null; tag: string | null }> = [];
  for (const [path, rawItem] of Object.entries(asObj(doc.paths) ?? {})) {
    const item = asObj(rawItem);
    if (!item) continue;
    for (const m of ['get', 'post', 'put', 'patch', 'delete', 'head', 'options']) {
      const op = asObj(item[m]);
      if (!op) continue;
      operations.push({
        method: m.toUpperCase(),
        path,
        summary: asStr(op.summary),
        tag: asStr(asArr(op.tags)[0]),
      });
    }
  }
  operations.sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method));

  return (
    <div className="spec-view">
      <div className="spec-meta">
        <div>
          <strong>{spec.title ?? data.product.name}</strong>
          {spec.apiVersion && <span className="ver"> {spec.apiVersion}</span>}
          {spec.specFormat && <span className="muted small"> · OpenAPI {spec.specFormat}</span>}
        </div>
        <div className="muted small">
          {data.name} on {data.server.name}
          {data.client && <> · {data.client.code}</>} · {spec.operationCount} operations ·{' '}
          {(spec.byteSize / 1024).toFixed(0)} KB
        </div>
        <div className="muted small">
          {/* Said plainly. This is a stored copy, and a reader who assumes it is
              live would draw the wrong conclusion from a stale one. */}
          Captured {new Date(spec.fetchedAt).toLocaleString()} from{' '}
          <code className="mono">{spec.sourceUrl}</code>
        </div>
        <div className="spec-actions">
          {data.apiSpecs.length > 1 && (
            <select value={which} onChange={(e) => setWhich(Number(e.target.value))}>
              {data.apiSpecs.map((s, i) => (
                <option key={s.sourceUrl} value={i}>
                  {s.sourceUrl}
                </option>
              ))}
            </select>
          )}
          <button type="button" className="ghost" onClick={() => setRaw((r) => !r)}>
            {raw ? 'Formatted' : 'Raw JSON'}
          </button>
        </div>
      </div>

      {raw ? (
        <pre className="spec-raw">{JSON.stringify(doc, null, 2)}</pre>
      ) : selected ? (
        <>
          <button type="button" className="linklike" onClick={() => setSelected(null)}>
            ← All {operations.length} operations
          </button>
          <OperationDetail doc={doc} method={selected.method} path={selected.path} />
        </>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Method</th>
              <th scope="col">Path</th>
              <th scope="col">Summary</th>
            </tr>
          </thead>
          <tbody>
            {operations.map((o) => (
              <tr key={`${o.method} ${o.path}`}>
                <td>
                  <span className={`method method-${o.method.toLowerCase()}`}>{o.method}</span>
                </td>
                <td>
                  <button
                    type="button"
                    className="linklike mono"
                    onClick={() => setSelected({ method: o.method, path: o.path })}
                  >
                    {o.path}
                  </button>
                </td>
                <td className="small">{o.summary ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
